import { useState, useEffect } from "react";
import { Cpu, Play, Square, RefreshCw, Activity, CheckCircle2, XCircle } from "lucide-react";
import { API_BASE } from "@/lib/api";

export default function BotControlPage() {
  const [status, setStatus] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [selectedMode, setSelectedMode] = useState<string>("hybrid");

  const fetchStatus = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/bot/status`);
      const data = await res.json();
      setStatus(data);
      if (data.mode) setSelectedMode(data.mode);
    } catch (e) {
      setStatus({ error: "API unreachable" });
    }
  };

  const startBot = async () => {
    setLoading(true);
    await fetch(`${API_BASE}/api/bot/start?mode=${selectedMode}`, { method: "POST" });
    await fetchStatus();
    setLoading(false);
  };

  const stopBot = async () => {
    setLoading(true);
    await fetch(`${API_BASE}/api/bot/stop`, { method: "POST" });
    await fetchStatus();
    setLoading(false);
  };

  const runCycle = async () => {
    setLoading(true);
    await fetch(`${API_BASE}/api/bot/cycle`, { method: "POST" });
    await fetchStatus();
    setLoading(false);
  };

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 5000);
    return () => clearInterval(interval);
  }, []);

  const isRunning = status?.running;

  return (
    <div className="p-6 space-y-6 max-w-4xl">
      <div className="flex items-center gap-3">
        <Cpu size={22} className="text-terminal-accent" />
        <div>
          <h1 className="text-xl font-bold text-white">BOT CONTROL</h1>
          <p className="text-xs text-gray-500">Start, stop, and monitor the multi-agent trading engine</p>
        </div>
      </div>

      {/* Status Panel */}
      <div className="terminal-window p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className={`w-3 h-3 rounded-full ${isRunning ? "bg-green-500 animate-pulse" : "bg-red-500"}`} />
            <span className="text-lg font-bold text-white">
              {isRunning ? "RUNNING" : "STOPPED"}
            </span>
          </div>
          <div className="text-xs text-gray-500">
            {status?.last_cycle ? `Last cycle: ${new Date(status.last_cycle * 1000).toLocaleTimeString()}` : "No cycles yet"}
          </div>
        </div>

        {/* Mode Selector */}
        <div className="mb-4">
          <label className="text-xs text-gray-500 block mb-2">CREW MODE</label>
          <div className="flex gap-2">
            {[
              { id: "directional", label: "DIRECTIONAL" },
              { id: "auto_lp", label: "AUTO LP" },
              { id: "hybrid", label: "HYBRID" },
            ].map((m) => (
              <button
                key={m.id}
                onClick={() => setSelectedMode(m.id)}
                disabled={isRunning}
                className={`px-3 py-1.5 border rounded text-xs cursor-pointer transition-all ${
                  selectedMode === m.id
                    ? "border-terminal-accent text-terminal-accent bg-terminal-accent/10"
                    : "border-terminal-border text-gray-500 hover:text-gray-300"
                }`}
              >
                {m.label}
              </button>
            ))}
          </div>
          <p className="text-[10px] text-gray-500 mt-1">
            {selectedMode === "directional" && "Trade on price gaps and edge. Active buying/selling."}
            {selectedMode === "auto_lp" && "Passive liquidity provision. Earn LP rewards 24/7."}
            {selectedMode === "hybrid" && "Both directional trades + LP farming simultaneously."}
          </p>
        </div>

        {/* Control Buttons */}
        <div className="flex gap-3">
          {!isRunning ? (
            <button onClick={startBot} disabled={loading} className="terminal-btn-primary flex items-center gap-2">
              {loading ? <RefreshCw size={14} className="animate-spin" /> : <Play size={14} />}
              START BOT
            </button>
          ) : (
            <>
              <button onClick={stopBot} disabled={loading} className="terminal-btn-danger flex items-center gap-2">
                {loading ? <RefreshCw size={14} className="animate-spin" /> : <Square size={14} />}
                STOP BOT
              </button>
              {status?.mode === "auto_lp" || status?.mode === "hybrid" ? (
                <button
                  onClick={async () => {
                    await fetch(`${API_BASE}/api/bot/lp/cancel-all`, { method: "POST" });
                    await fetchStatus();
                  }}
                  className="terminal-btn text-xs border-yellow-600 text-yellow-400"
                >
                  CANCEL ALL LP
                </button>
              ) : null}
            </>
          )}
          <button onClick={runCycle} disabled={loading || !isRunning} className="terminal-btn flex items-center gap-2">
            <Activity size={14} />
            RUN CYCLE
          </button>
        </div>
      </div>

      {/* Latest Decision */}
      <div className="terminal-window">
        <div className="terminal-header">
          <span className="flex items-center gap-2"><CheckCircle2 size={12} /> LATEST DECISION</span>
        </div>
        <div className="p-4">
          {status?.latest_payload ? (
            <pre className="text-xs text-green-400 font-mono overflow-x-auto">
              {JSON.stringify(status.latest_payload, null, 2)}
            </pre>
          ) : (
            <div className="text-gray-500 text-sm">No decisions yet. Start the bot to begin trading.</div>
          )}
        </div>
      </div>

      {/* Summary */}
      {status?.scraper_summary && (
        <div className="terminal-window">
          <div className="terminal-header">
            <span className="flex items-center gap-2"><Activity size={12} /> SCRAPER SUMMARY</span>
          </div>
          <div className="p-4 text-sm text-gray-300 leading-relaxed">
            {status.scraper_summary}
          </div>
        </div>
      )}
    </div>
  );
}
